import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Stethoscope,
  Users,
  CreditCard,
  Calendar,
  ChevronDown,
  RefreshCw,
  Crown,
  AlertCircle,
} from "lucide-react";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import "../components/Dashboard/chartSetup";
import {
  getSubscriptionDashboardStats,
  SubscriptionDashboardStats,
} from "../services/subscriptionService";

// Helper for Indian compact currency format on Y-axis
const formatInrCompact = (val: number): string => {
  if (val === 0) return "₹ 0";
  if (val >= 10000000) return `₹ ${(val / 10000000).toFixed(val % 10000000 === 0 ? 0 : 1)} Cr`;
  if (val >= 100000) return `₹ ${(val / 100000).toFixed(val % 100000 === 0 ? 0 : 1)}L`;
  if (val >= 1000) return `₹ ${(val / 1000).toFixed(val % 1000 === 0 ? 0 : 1)}k`;
  return `₹ ${val}`;
};

// Date Presets
const PRESET_OPTIONS = [
  { value: "last30", label: "Last 30 Days" },
  { value: "last90", label: "Last 90 Days" },
  { value: "last180", label: "Last 6 Months" },
  { value: "thisYear", label: "This Year" },
  { value: "all", label: "All Time" },
  { value: "custom", label: "Custom Range" },
];

const PLAN_COLORS = [
  "#8B5CF6", // Purple (Annual Starter)
  "#15BBAE", // Teal (Enterprise)
  "#0284C7", // Blue (Starter)
  "#F59E0B", // Orange (Basic)
  "#EC4899", // Pink (Basic)
  "#10B981", // Emerald
  "#6366F1", // Indigo
];

export const SubscriptionDashboard: React.FC = () => {
  const [stats, setStats] = useState<SubscriptionDashboardStats | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [selectedPreset, setSelectedPreset] = useState<string>("last180");
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const [customStart, setCustomStart] = useState<string>("");
  const [customEnd, setCustomEnd] = useState<string>("");

  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const fetchStats = async (presetVal: string, start?: string, end?: string) => {
    try {
      setLoading(true);
      setError(null);
      const params: { preset?: string; startDate?: string; endDate?: string } = {};

      if (presetVal === "custom" && start && end) {
        params.startDate = start;
        params.endDate = end;
      } else {
        params.preset = presetVal;
      }

      const data = await getSubscriptionDashboardStats(params);
      setStats(data);
    } catch (err: any) {
      console.error("Error fetching subscription dashboard stats:", err);
      setError(err?.response?.data?.message || "Failed to load dashboard data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats(selectedPreset);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPreset]);

  const handlePresetSelect = (presetVal: string) => {
    setSelectedPreset(presetVal);
    if (presetVal !== "custom") {
      setDropdownOpen(false);
    }
  };

  const handleApplyCustom = (e: React.FormEvent) => {
    e.preventDefault();
    if (!customStart || !customEnd) return;
    setSelectedPreset("custom");
    setDropdownOpen(false);
    fetchStats("custom", customStart, customEnd);
  };

  // 1. Subscription Revenue Trend Chart Config
  const revenueTrendData = useMemo(() => {
    if (!stats || !stats.revenueTrend) return { labels: [], datasets: [] };

    const labels = stats.revenueTrend.map((t) => t.month);
    return {
      labels,
      datasets: [
        {
          label: "Doctor Revenue",
          data: stats.revenueTrend.map((t) => t.doctorRevenue),
          borderColor: "#15BBAE",
          backgroundColor: "rgba(21, 187, 174, 0.12)",
          fill: true,
          tension: 0.35,
          borderWidth: 2.5,
          pointRadius: 4,
          pointBackgroundColor: "#15BBAE",
          pointHoverRadius: 6,
        },
        {
          label: "Public User Revenue",
          data: stats.revenueTrend.map((t) => t.publicRevenue),
          borderColor: "#8B5CF6",
          backgroundColor: "rgba(139, 92, 246, 0.08)",
          fill: true,
          tension: 0.35,
          borderWidth: 2.5,
          pointRadius: 4,
          pointBackgroundColor: "#8B5CF6",
          pointHoverRadius: 6,
        },
      ],
    };
  }, [stats]);

  const revenueTrendOptions: any = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: "#16181D",
          padding: 12,
          titleColor: "#FFFFFF",
          bodyColor: "rgba(255,255,255,0.9)",
          footerColor: "rgba(255,255,255,0.75)",
          cornerRadius: 8,
          callbacks: {
            title: (items: any) => {
              if (!items || items.length === 0) return "";
              const idx = items[0].dataIndex;
              return stats?.revenueTrend?.[idx]?.fullMonth || items[0].label || "";
            },
            label: (ctx: any) => {
              const label = ctx.dataset.label || "";
              const val = ctx.parsed.y || 0;
              return `${label}: ₹ ${new Intl.NumberFormat("en-IN").format(val)}`;
            },
            footer: (items: any) => {
              if (!items || items.length === 0) return "";
              const total = items.reduce((acc: number, curr: any) => acc + (curr.parsed.y || 0), 0);
              return `Total: ₹ ${new Intl.NumberFormat("en-IN").format(total)}`;
            },
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: {
            color: "#63788E",
            font: { size: 12 },
            maxTicksLimit: 12,
            autoSkip: true,
          },
        },
        y: {
          beginAtZero: true,
          grid: { color: "#F0F4F8" },
          ticks: {
            color: "#63788E",
            font: { size: 12 },
            callback: (val: any) => formatInrCompact(Number(val)),
          },
        },
      },
    }),
    [stats]
  );

  // 2. Plan Distribution Chart Config (Grouped Bar Chart)
  const planDistributionData = useMemo(() => {
    if (!stats || !stats.planDistribution) return { labels: [], datasets: [] };

    const labels = stats.planDistribution.map((p) => p.planName);
    return {
      labels,
      datasets: [
        {
          label: "Doctor",
          data: stats.planDistribution.map((p) => p.doctors),
          backgroundColor: "#15BBAE",
          borderRadius: 4,
          barPercentage: 0.65,
          categoryPercentage: 0.6,
        },
        {
          label: "Public User",
          data: stats.planDistribution.map((p) => p.publicUsers),
          backgroundColor: "#8B5CF6",
          borderRadius: 4,
          barPercentage: 0.65,
          categoryPercentage: 0.6,
        },
      ],
    };
  }, [stats]);

  const barValuePlugin = useMemo(() => {
    return {
      id: "planBarValues",
      afterDatasetsDraw: (chart: any) => {
        const ctx = chart.ctx;
        ctx.save();
        ctx.font = "600 11px Inter, sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "bottom";
        ctx.fillStyle = "#63788E";

        chart.data.datasets.forEach((dataset: any, datasetIndex: number) => {
          const meta = chart.getDatasetMeta(datasetIndex);
          if (meta.hidden) return;
          meta.data.forEach((element: any, index: number) => {
            const val = dataset.data[index];
            if (val !== undefined && val !== null && val > 0) {
              ctx.fillText(String(val), element.x, element.y - 4);
            }
          });
        });
        ctx.restore();
      },
    };
  }, []);

  const planDistributionOptions: any = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: "#16181D",
          padding: 10,
          titleColor: "#FFFFFF",
          bodyColor: "rgba(255,255,255,0.9)",
          cornerRadius: 6,
          callbacks: {
            label: (ctx: any) => {
              const label = ctx.dataset.label || "";
              const val = ctx.parsed.y || 0;
              return `${label}: ${val} subscriptions`;
            },
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { color: "#63788E", font: { size: 12 } },
        },
        y: {
          beginAtZero: true,
          ticks: { precision: 0, color: "#63788E", font: { size: 12 } },
          grid: { color: "#F0F4F8" },
        },
      },
    }),
    []
  );

  // 3. Subscription Usage Donut Chart Plugin & Config
  const donutPlugin = useMemo(() => {
    return {
      id: "subscriptionUsageCenterText",
      afterDatasetsDraw: (chart: any) => {
        let x = chart.width / 2;
        let y = chart.height / 2;
        if (chart.chartArea) {
          x = (chart.chartArea.left + chart.chartArea.right) / 2;
          y = (chart.chartArea.top + chart.chartArea.bottom) / 2;
        } else {
          const meta = chart.getDatasetMeta(0);
          if (meta?.data?.[0]) {
            x = meta.data[0].x;
            y = meta.data[0].y;
          }
        }

        const opts =
          chart.config?.options?.plugins?.subscriptionUsageCenterText ||
          chart.options?.plugins?.subscriptionUsageCenterText;

        const total =
          opts?.total !== undefined
            ? opts.total
            : (chart.data?.datasets?.[0]?.data?.reduce(
                (acc: number, val: any) => acc + (Number(val) || 0),
                0
              ) ?? 0);

        const ctx = chart.ctx;
        ctx.save();
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";

        // Count
        ctx.fillStyle = "#10253A";
        ctx.font = "700 24px Inter, sans-serif";
        ctx.fillText(Number(total).toLocaleString("en-IN"), x, y - 8);

        // Subtitle
        ctx.fillStyle = "#63788E";
        ctx.font = "500 11px Inter, sans-serif";
        ctx.fillText("Total Subscriptions", x, y + 14);

        ctx.restore();
      },
    };
  }, []);

  const usageChartData = useMemo(() => {
    if (!stats || !stats.subscriptionUsage?.statuses) return { labels: [], datasets: [] };

    const statuses = stats.subscriptionUsage.statuses;
    return {
      labels: statuses.map((s) => s.label),
      datasets: [
        {
          data: statuses.map((s) => s.count),
          backgroundColor: statuses.map((s) => s.color),
          borderColor: "#FFFFFF",
          borderWidth: 2,
        },
      ],
    };
  }, [stats]);

  const usageChartOptions: any = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: true,
      aspectRatio: 1,
      cutout: "72%",
      plugins: {
        legend: { display: false },
        subscriptionUsageCenterText: {
          total: stats?.subscriptionUsage?.total ?? 0,
        },
        tooltip: {
          backgroundColor: "#16181D",
          padding: 10,
          cornerRadius: 6,
          callbacks: {
            label: (ctx: any) => {
              const item = stats?.subscriptionUsage?.statuses[ctx.dataIndex];
              if (!item) return `${ctx.label}: ${ctx.parsed}`;
              const byType = item.byType
                ? ` (Doctor: ${item.byType.doctor || 0}, Public: ${item.byType.public || 0})`
                : "";
              return `${item.label}: ${item.count} (${item.percentage}%)${byType}`;
            },
          },
        },
      },
    }),
    [stats]
  );


  return (
    <div className="space-y-6 pb-12">
      {/* ── Top Header Section ── */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3.5">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#E6F7F5] text-[#15BBAE] shadow-sm">
            <Crown size={22} className="stroke-[2.2]" />
          </div>
          <div>
            <h1 className="text-xl font-bold tracking-tight text-[#10253A] sm:text-2xl">
              Subscription Dashboard
            </h1>
            <p className="text-xs font-medium text-[#63788E] sm:text-sm">
              Overview of doctor and public user subscriptions, usage and billing details
            </p>
          </div>
        </div>

        {/* Date Range Selector Dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            type="button"
            id="date-range-btn"
            data-testid="date-range-btn"
            onClick={() => setDropdownOpen((prev) => !prev)}
            className="inline-flex items-center gap-2.5 rounded-lg border border-[#E4EAF0] bg-white px-4 py-2.5 text-xs font-medium text-[#10253A] shadow-sm transition-all hover:bg-neutral-50 sm:text-sm"
          >
            {loading ? (
              <RefreshCw size={15} className="animate-spin text-[#15BBAE]" />
            ) : (
              <Calendar size={16} className="text-[#15BBAE]" />
            )}
            <span id="date-range-label">{stats?.dateRange?.label || "Select Date Range"}</span>
            <ChevronDown
              size={15}
              className={`text-[#63788E] transition-transform duration-200 ${
                dropdownOpen ? "rotate-180" : ""
              }`}
            />
          </button>

          {dropdownOpen && (
            <div className="absolute right-0 z-30 mt-2 w-72 rounded-xl border border-[#E4EAF0] bg-white p-2 shadow-xl animate-in fade-in zoom-in-95">
              <div className="space-y-1">
                {PRESET_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => handlePresetSelect(opt.value)}
                    className={`w-full flex items-center justify-between px-3 py-2 text-xs font-medium rounded-lg text-left transition-colors ${
                      selectedPreset === opt.value
                        ? "bg-[#E6F7F5] text-[#15BBAE] font-semibold"
                        : "text-[#10253A] hover:bg-[#F7F9FB]"
                    }`}
                  >
                    <span>{opt.label}</span>
                    {selectedPreset === opt.value && (
                      <span className="h-1.5 w-1.5 rounded-full bg-[#15BBAE]" />
                    )}
                  </button>
                ))}
              </div>

              {selectedPreset === "custom" && (
                <form
                  onSubmit={handleApplyCustom}
                  className="mt-2.5 border-t border-[#F0F4F8] pt-2.5 px-1 space-y-2"
                >
                  <div>
                    <label className="block text-[11px] font-medium text-[#63788E] mb-1">
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={customStart}
                      onChange={(e) => setCustomStart(e.target.value)}
                      required
                      className="w-full rounded-lg border border-[#E4EAF0] px-2.5 py-1.5 text-xs text-[#10253A] focus:border-[#15BBAE] focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-medium text-[#63788E] mb-1">
                      End Date
                    </label>
                    <input
                      type="date"
                      value={customEnd}
                      onChange={(e) => setCustomEnd(e.target.value)}
                      required
                      className="w-full rounded-lg border border-[#E4EAF0] px-2.5 py-1.5 text-xs text-[#10253A] focus:border-[#15BBAE] focus:outline-none"
                    />
                  </div>
                  <div className="flex items-center gap-2 pt-1">
                    <button
                      type="submit"
                      className="flex-1 rounded-lg bg-[#15BBAE] py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-[#13a89d] transition-colors"
                    >
                      Apply
                    </button>
                    <button
                      type="button"
                      onClick={() => setDropdownOpen(false)}
                      className="rounded-lg border border-[#E4EAF0] px-3 py-1.5 text-xs font-medium text-[#63788E] hover:bg-neutral-50 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Error Banner ── */}
      {error && (
        <div className="flex items-center justify-between rounded-xl border border-red-200 bg-red-50 p-4 text-red-700">
          <div className="flex items-center gap-3">
            <AlertCircle size={20} className="shrink-0 text-red-500" />
            <span className="text-sm font-medium">{error}</span>
          </div>
          <button
            type="button"
            onClick={() => fetchStats(selectedPreset)}
            className="flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-red-700 transition-colors"
          >
            <RefreshCw size={14} />
            Retry
          </button>
        </div>
      )}

      {/* ── Loading Skeleton / KPI Widgets ── */}
      {loading && !stats ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="h-40 rounded-xl border border-[#E4EAF0] bg-white p-5 animate-pulse"
            />
          ))}
        </div>
      ) : (
        stats && (
          <>
            {/* ── 1. Four KPI Widgets (Doctor, Public Users, Total Subscriptions, Total Revenue) ── */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {/* Card 1: Total Doctors */}
              <div className="flex flex-col justify-between rounded-xl border border-[#E4EAF0] bg-white p-5 shadow-sm transition-all hover:shadow-md">
                <div>
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-xs font-medium text-[#63788E]">Total Doctors</p>
                      <h3 className="mt-1 text-3xl font-bold tracking-tight text-[#10253A]">
                        {stats.kpis.totalDoctors.count.toLocaleString("en-IN")}
                      </h3>
                    </div>
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#EDE9FE] text-[#7C3AED]">
                      <Stethoscope size={20} />
                    </div>
                  </div>
                </div>

                <div className="mt-4 border-t border-[#F0F4F8] pt-3 flex items-center gap-2.5 text-[11px] text-[#63788E]">
                  <span className="flex items-center gap-1">
                    <span className="h-2 w-2 rounded-full bg-[#15BBAE]" />
                    <strong className="text-[#10253A] font-semibold">
                      {stats.kpis.totalDoctors.breakdown.active.toLocaleString("en-IN")}
                    </strong>{" "}
                    Active
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="h-2 w-2 rounded-full bg-[#F59E0B]" />
                    <strong className="text-[#10253A] font-semibold">
                      {stats.kpis.totalDoctors.breakdown.pending.toLocaleString("en-IN")}
                    </strong>{" "}
                    Pending
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="h-2 w-2 rounded-full bg-[#E84570]" />
                    <strong className="text-[#10253A] font-semibold">
                      {stats.kpis.totalDoctors.breakdown.expired.toLocaleString("en-IN")}
                    </strong>{" "}
                    Expired
                  </span>
                </div>
              </div>

              {/* Card 2: Total Public Users */}
              <div className="flex flex-col justify-between rounded-xl border border-[#E4EAF0] bg-white p-5 shadow-sm transition-all hover:shadow-md">
                <div>
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-xs font-medium text-[#63788E]">Total Public Users</p>
                      <h3 className="mt-1 text-3xl font-bold tracking-tight text-[#10253A]">
                        {stats.kpis.totalPublicUsers.count.toLocaleString("en-IN")}
                      </h3>
                    </div>
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#E0F2FE] text-[#0284C7]">
                      <Users size={20} />
                    </div>
                  </div>
                </div>

                <div className="mt-4 border-t border-[#F0F4F8] pt-3 flex items-center gap-2.5 text-[11px] text-[#63788E]">
                  <span className="flex items-center gap-1">
                    <span className="h-2 w-2 rounded-full bg-[#15BBAE]" />
                    <strong className="text-[#10253A] font-semibold">
                      {stats.kpis.totalPublicUsers.breakdown.active.toLocaleString("en-IN")}
                    </strong>{" "}
                    Active
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="h-2 w-2 rounded-full bg-[#F59E0B]" />
                    <strong className="text-[#10253A] font-semibold">
                      {stats.kpis.totalPublicUsers.breakdown.pending.toLocaleString("en-IN")}
                    </strong>{" "}
                    Pending
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="h-2 w-2 rounded-full bg-[#E84570]" />
                    <strong className="text-[#10253A] font-semibold">
                      {stats.kpis.totalPublicUsers.breakdown.expired.toLocaleString("en-IN")}
                    </strong>{" "}
                    Expired
                  </span>
                </div>
              </div>

              {/* Card 3: Total Subscriptions */}
              <div className="flex flex-col justify-between rounded-xl border border-[#E4EAF0] bg-white p-5 shadow-sm transition-all hover:shadow-md">
                <div>
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-xs font-medium text-[#63788E]">Total Subscriptions</p>
                      <h3 className="mt-1 text-3xl font-bold tracking-tight text-[#10253A]">
                        {stats.kpis.totalSubscriptions.count.toLocaleString("en-IN")}
                      </h3>
                    </div>
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#FDF2F8] text-[#DB2777]">
                      <CreditCard size={20} />
                    </div>
                  </div>
                </div>

                <div className="mt-4 border-t border-[#F0F4F8] pt-3 flex items-center gap-2.5 text-[11px] text-[#63788E]">
                  <span className="flex items-center gap-1">
                    <span className="h-2 w-2 rounded-full bg-[#15BBAE]" />
                    <strong className="text-[#10253A] font-semibold">
                      {stats.kpis.totalSubscriptions.breakdown.active.toLocaleString("en-IN")}
                    </strong>{" "}
                    Active
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="h-2 w-2 rounded-full bg-[#F59E0B]" />
                    <strong className="text-[#10253A] font-semibold">
                      {stats.kpis.totalSubscriptions.breakdown.pending.toLocaleString("en-IN")}
                    </strong>{" "}
                    Pending
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="h-2 w-2 rounded-full bg-[#E84570]" />
                    <strong className="text-[#10253A] font-semibold">
                      {stats.kpis.totalSubscriptions.breakdown.expired.toLocaleString("en-IN")}
                    </strong>{" "}
                    Expired
                  </span>
                </div>
              </div>

              {/* Card 4: Total Revenue (This Period) */}
              <div className="flex flex-col justify-between rounded-xl border border-[#E4EAF0] bg-white p-5 shadow-sm transition-all hover:shadow-md">
                <div>
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-xs font-medium text-[#63788E]">Total Revenue (This Period)</p>
                      <h3 className="mt-1 text-3xl font-bold tracking-tight text-[#10253A]">
                        {stats.kpis.totalRevenue.formatted}
                      </h3>
                    </div>
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#ECFDF5] text-[#059669] font-bold text-lg">
                      ₹
                    </div>
                  </div>
                </div>

                <div className="mt-4 border-t border-[#F0F4F8] pt-3 text-[11px] text-[#63788E] flex flex-wrap items-center justify-between gap-1">
                  <span>
                    Doctor:{" "}
                    <strong className="text-[#10253A] font-semibold">
                      {stats.kpis.totalRevenue.breakdown.doctorRevenueFormatted}
                    </strong>
                  </span>
                  <span>
                    Public:{" "}
                    <strong className="text-[#10253A] font-semibold">
                      {stats.kpis.totalRevenue.breakdown.publicRevenueFormatted}
                    </strong>
                  </span>
                </div>
              </div>
            </div>

            {/* ── 2. Middle Section: Subscription Usage & Plan Distribution ── */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
              {/* Chart 1: Subscription Usage (Left - 5 cols) */}
              <div className="lg:col-span-5 rounded-xl border border-[#E4EAF0] bg-white p-5 shadow-sm">
                <div className="pb-4 border-b border-[#F0F4F8]">
                  <h3 className="text-sm font-semibold text-[#10253A]">Subscription Usage</h3>
                  <p className="text-[11px] text-[#63788E]">Status breakdown across all subscriptions</p>
                </div>

                <div className="mt-4 flex flex-col items-center justify-center">
                  <div className="relative h-44 w-44">
                    <Doughnut
                      key={`subscription-usage-donut-${selectedPreset}-${stats?.dateRange?.startDate}-${stats?.dateRange?.endDate}-${stats?.subscriptionUsage?.total ?? 0}`}
                      data={usageChartData}
                      options={usageChartOptions}
                      plugins={[donutPlugin]}
                    />
                  </div>

                  {/* Status Legend on the bottom/side */}
                  <ul className="mt-4 w-full max-w-sm space-y-2 border-t border-[#F0F4F8] pt-3">
                    {stats.subscriptionUsage.statuses.map((st) => (
                      <li
                        key={st.label}
                        className="flex items-center justify-between text-xs text-[#10253A]"
                      >
                        <div className="flex items-center gap-2">
                          <span
                            className="h-2.5 w-2.5 rounded-full"
                            style={{ backgroundColor: st.color }}
                          />
                          <span className="font-medium text-[#63788E]">{st.label}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-[#10253A]">
                            {st.count.toLocaleString("en-IN")}
                          </span>
                          <span className="w-9 text-right text-[11px] font-medium text-[#63788E]">
                            ({st.percentage}%)
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Chart 2: Plan Distribution (Right - 7 cols) */}
              <div className="lg:col-span-7 rounded-xl border border-[#E4EAF0] bg-white p-5 shadow-sm">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pb-4 border-b border-[#F0F4F8]">
                  <div>
                    <h3 className="text-sm font-semibold text-[#10253A]">Plan Distribution</h3>
                    <p className="text-[11px] text-[#63788E]">Doctor & Public User Subscriptions</p>
                  </div>
                  <div className="flex items-center gap-2.5 text-xs text-[#63788E]">
                    <span className="flex items-center gap-1">
                      <span className="h-2 w-2 rounded-sm bg-[#15BBAE]" />
                      Doctor
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="h-2 w-2 rounded-sm bg-[#8B5CF6]" />
                      Public User
                    </span>
                  </div>
                </div>

                <div className="relative mt-4 h-64 w-full">
                  <Bar
                    key={`plan-distribution-bar-${selectedPreset}-${stats?.dateRange?.startDate}-${stats?.dateRange?.endDate}`}
                    data={planDistributionData}
                    options={planDistributionOptions}
                    plugins={[barValuePlugin]}
                  />
                </div>
              </div>
            </div>

            {/* ── 3. Bottom Section: Top Revenue Generating Plans Table & Subscription Revenue Trend ── */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
              {/* Bottom Left Card: Top Revenue Generating Plans Table (5 cols) */}
              <div className="lg:col-span-5 rounded-xl border border-[#E4EAF0] bg-white p-5 shadow-sm">
                <div className="pb-3 border-b border-[#F0F4F8]">
                  <h3 className="text-sm font-bold text-[#10253A]">
                    Top Revenue Generating Plans
                  </h3>
                </div>

                <div className="mt-3 overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead>
                      <tr className="border-b border-[#F0F4F8] text-xs font-semibold text-[#63788E]">
                        <th className="pb-3 pl-1 font-semibold">Plan Name</th>
                        <th className="pb-3 text-center font-semibold">Subscriptions</th>
                        <th className="pb-3 pr-1 text-right font-semibold">Revenue</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#F0F4F8]">
                      {stats.topPlans && stats.topPlans.length > 0 ? (
                        stats.topPlans.map((plan, idx) => (
                          <tr
                            key={plan.planId || idx}
                            className="hover:bg-[#F8FAFC] transition-colors"
                          >
                            <td className="py-3.5 pl-1 font-medium text-[#10253A]">
                              <div className="flex items-center gap-2.5">
                                <span
                                  className="h-2.5 w-2.5 rounded-full shrink-0"
                                  style={{ backgroundColor: PLAN_COLORS[idx % PLAN_COLORS.length] }}
                                />
                                <span className="text-[13px] font-medium text-[#10253A]">
                                  {plan.planName}
                                </span>
                              </div>
                            </td>
                            <td className="py-3.5 text-center text-[13px] font-medium text-[#10253A]">
                              {plan.subscriptions}
                            </td>
                            <td className="py-3.5 pr-1 text-right text-[13px] font-bold text-[#10253A]">
                              {plan.revenueFormatted}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={3} className="py-6 text-center text-xs text-[#63788E]">
                            No plans available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Bottom Right Card: Subscription Revenue Trend (7 cols) */}
              <div className="lg:col-span-7 rounded-xl border border-[#E4EAF0] bg-white p-5 shadow-sm">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pb-3 border-b border-[#F0F4F8]">
                  <div>
                    <h3 className="text-sm font-bold text-[#10253A]">
                      Subscription Revenue Trend
                    </h3>
                    <p className="text-[11px] text-[#63788E] mt-0.5">
                      Monthly doctor & public user subscription revenue
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 text-xs text-[#63788E]">
                    <span className="flex items-center gap-1.5">
                      <span className="h-2.5 w-2.5 rounded-full bg-[#15BBAE]" />
                      Doctor Revenue
                    </span>
                    <span className="flex items-center gap-1.5">
                      <span className="h-2.5 w-2.5 rounded-full bg-[#8B5CF6]" />
                      Public User Revenue
                    </span>
                  </div>
                </div>

                <div className="relative mt-4 h-64 sm:h-72 w-full">
                  <Line
                    key={`revenue-trend-line-${selectedPreset}-${stats?.dateRange?.startDate}-${stats?.dateRange?.endDate}`}
                    data={revenueTrendData}
                    options={revenueTrendOptions}
                  />
                </div>
              </div>
            </div>
          </>
        )
      )}
    </div>
  );
};

export default SubscriptionDashboard;
